'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { useTranslation } from 'react-i18next';

import { useLocalized } from '~/(tenant)/_components/native/_lib/use-localized';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  DirectionalArrow,
  Eyebrow,
} from '~/(tenant)/_components/native/_primitives';

import type { HeroSectionConfig, HeroSlide } from '~/(tenant)/_lib/schema/native-home-config.schema';

/** A tenant-configured href that points outside the app. */
function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/** A safe navigation target: an in-app path or a full http(s) URL. */
function isSafeHref(href: string): boolean {
  return href.startsWith('/') || isExternalHref(href);
}

/**
 * Renders a hero slide's tap target. Internal routes use Next.js <Link>
 * (in-app navigation); external http(s) URLs use a plain anchor that opens in a
 * new tab so a tenant can point a slide at any external link without hijacking
 * the WebView. Mirrors ActionLink in quick-actions-section.tsx. Unsafe schemes
 * (javascript:, data:) degrade to a no-op `/` — the save-time schema rejects
 * them, this is the render-time backstop.
 */
function SlideLink({
  href,
  style,
  ariaLabel,
  ariaHidden,
  children,
}: {
  href: string;
  style: React.CSSProperties;
  ariaLabel: string;
  ariaHidden?: boolean;
  children: React.ReactNode;
}) {
  const safeHref = isSafeHref(href) ? href : '/';

  if (isExternalHref(safeHref)) {
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
        aria-label={ariaLabel}
        aria-hidden={ariaHidden}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={safeHref}
      style={style}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      {children}
    </Link>
  );
}

interface HeroSectionProps {
  config: HeroSectionConfig;
  accountId: string;
  storageUrl: string;
  /** UUID of the active venue resolved server-side from the location-switcher
   *  cookie. Slides tagged with a `locationId` only render when it matches;
   *  untagged slides are global and always render. */
  activeLocationId?: string | null;
}

/**
 * Filters slides to those visible for the active venue: a slide shows when it
 * has no `locationId` (global) or its tag matches `activeLocationId`. When no
 * venue is selected, only global slides show — a location-tagged slide should
 * never leak before the user has picked a venue.
 *
 * Guards against an empty result (e.g. every slide is tagged to other venues)
 * by falling back to the global slides, and finally to all slides, so the hero
 * never collapses to zero and `slides[0]!` stays safe.
 */
function selectVisibleSlides(
  slides: HeroSlide[],
  activeLocationId: string | null | undefined,
): HeroSlide[] {
  const visible = slides.filter(
    (slide) =>
      !slide.locationId ||
      (activeLocationId != null && slide.locationId === activeLocationId),
  );

  if (visible.length > 0) return visible;

  const global = slides.filter((slide) => !slide.locationId);
  return global.length > 0 ? global : slides;
}

export function HeroSection({
  config,
  accountId,
  storageUrl,
  activeLocationId = null,
}: HeroSectionProps) {
  const heightVh = config.heightVh ?? 0.45;
  const slides = selectVisibleSlides(config.slides, activeLocationId);

  if (config.mode === 'static' || slides.length === 1) {
    return (
      <SingleHero
        slide={slides[0]!}
        accountId={accountId}
        storageUrl={storageUrl}
        heightVh={heightVh}
        priority
      />
    );
  }

  return (
    <CarouselHero
      slides={slides}
      accountId={accountId}
      storageUrl={storageUrl}
      heightVh={heightVh}
    />
  );
}

function resolveImageUrl(storageUrl: string, accountId: string, imageKey: string) {
  return `${storageUrl}/storage/v1/object/public/home_assets/${accountId}/${imageKey}`;
}

/**
 * Magazine layout for a single slide. Composes:
 *   - Top eyebrow row (eyebrow ↔ edition)
 *   - Bottom-left: kicker → headline → italic emphasis → CTA pill
 *   - Bottom-left caption (low opacity)
 *
 * Falls back gracefully — a slide with only `imageKey` + `href` renders as
 * a plain clickable image with the standard gradient overlay.
 */
function SlideContent({
  slide,
  accountId,
  storageUrl,
  priority,
}: {
  slide: HeroSlide;
  accountId: string;
  storageUrl: string;
  priority?: boolean;
}) {
  const t = useNativeTokens();

  // Resolve display fields with backward compat: `headline` is the new field;
  // `label` is the legacy single-line title. If `headline` empty, use `label`.
  // Each field falls back to its `_ar` sibling under Arabic locale.
  const eyebrow = useLocalized(slide.eyebrow, slide.eyebrow_ar);
  const edition = useLocalized(slide.edition, slide.edition_ar);
  const kicker = useLocalized(slide.kicker, slide.kicker_ar);
  const headlineRaw = useLocalized(slide.headline, slide.headline_ar);
  const labelRaw = useLocalized(slide.label, slide.label_ar);
  const headline = headlineRaw ?? labelRaw;
  const emphasis = useLocalized(slide.emphasis, slide.emphasis_ar);
  const subtitle = useLocalized(slide.subtitle, slide.subtitle_ar);
  const ctaLabel = useLocalized(slide.ctaLabel, slide.ctaLabel_ar);
  const caption = useLocalized(slide.caption, slide.caption_ar);

  const hasOverlayContent = Boolean(
    eyebrow || edition || kicker || headline || emphasis || subtitle || ctaLabel || caption,
  );

  return (
    <>
      <Image
        src={resolveImageUrl(storageUrl, accountId, slide.imageKey)}
        alt={headline ?? slide.label ?? ''}
        fill
        className="object-cover"
        unoptimized
        priority={priority}
      />

      {/* Editorial gradient — image breathes at top, text reads at bottom. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, transparent 30%, rgba(0,0,0,0.55) 100%)',
        }}
      />


      {/* Top eyebrow row (masthead). Two corner-anchored cells. We position
          them with absolute corners (and force `dir=ltr`) so the eyebrow
          always sits visually left and the edition always sits visually
          right — the magazine masthead intent is geometric, not linguistic,
          so it should not flip with the document direction. */}
      {eyebrow && (
        <div
          dir="ltr"
          style={{
            position: 'absolute',
            top: 22,
            left: 22,
            maxWidth: 'calc(50% - 22px)',
          }}
        >
          <Eyebrow tone="inverse">{eyebrow}</Eyebrow>
        </div>
      )}
      {edition && (
        <div
          dir="ltr"
          style={{
            position: 'absolute',
            top: 22,
            right: 22,
            maxWidth: 'calc(50% - 22px)',
            textAlign: 'right',
          }}
        >
          <Eyebrow tone="inverse" align="end">
            {edition}
          </Eyebrow>
        </div>
      )}

      {/* Bottom-left composition. Forced LTR because the magazine layout is
          a geometric design intent (always bottom-left, headline reads
          downward) — not a linguistic one. RTL would right-align the whole
          stack and break the composition's gravity. */}
      {hasOverlayContent && (
        <div
          dir="ltr"
          style={{
            position: 'absolute',
            left: 22,
            right: 22,
            bottom: caption ? 36 : 22,
            color: '#fff',
            textAlign: 'left',
          }}
        >
          {kicker && (
            <Eyebrow tone="inverse" style={{ marginBottom: 10, opacity: 0.9 }}>
              {kicker}
            </Eyebrow>
          )}

          {(headline || emphasis) && (
            <h1
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 36,
                lineHeight: 1.04,
                fontWeight: 300,
                letterSpacing: '-0.01em',
                margin: 0,
              }}
            >
              {headline}
              {headline && emphasis ? <br /> : null}
              {emphasis && <em style={{ fontStyle: 'italic' }}>{emphasis}</em>}
            </h1>
          )}

          {subtitle && (
            <p
              style={{
                fontFamily: t.fontSans,
                fontSize: 13,
                marginTop: 10,
                opacity: 0.85,
                lineHeight: 1.5,
              }}
            >
              {subtitle}
            </p>
          )}

          {ctaLabel && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 18,
                padding: '11px 18px',
                background: t.accent,
                color: t.background,
                borderRadius: 999,
                fontFamily: t.fontSans,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {ctaLabel}
              <DirectionalArrow size={12} strokeWidth={1.5} color={t.background} />
            </span>
          )}
        </div>
      )}

      {/* Magazine credit caption */}
      {caption && (
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: 22,
            right: 22,
            fontFamily: t.fontMono,
            fontSize: 8,
            letterSpacing: '0.18em',
            color: 'rgba(255,255,255,0.45)',
            textTransform: 'uppercase',
          }}
        >
          {caption}
        </div>
      )}
    </>
  );
}

function SingleHero({
  slide,
  accountId,
  storageUrl,
  heightVh,
  priority,
}: {
  slide: HeroSlide;
  accountId: string;
  storageUrl: string;
  heightVh: number;
  priority?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section
      style={{
        position: 'relative',
        marginTop: 6,
        overflow: 'hidden',
        height: `${heightVh * 100}vh`,
      }}
    >
      <SlideLink
        href={slide.href}
        style={{ position: 'absolute', inset: 0, display: 'block' }}
        ariaLabel={slide.headline ?? slide.label ?? slide.ctaLabel ?? t('common:nativeHome.heroAriaLabel')}
      >
        <SlideContent slide={slide} accountId={accountId} storageUrl={storageUrl} priority={priority} />
      </SlideLink>
    </section>
  );
}

function CarouselHero({
  slides,
  accountId,
  storageUrl,
  heightVh,
}: {
  slides: HeroSlide[];
  accountId: string;
  storageUrl: string;
  heightVh: number;
}) {
  const { t } = useTranslation();
  // We render `slides` plus a clone of slides[0] at the end. When the track
  // animates onto the clone (index === slides.length), it visually matches
  // slide 0; we then snap back to index 0 without a transition so the loop
  // appears seamless.
  const [trackIndex, setTrackIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isDragging = useRef(false);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // The visible slide set can shrink at runtime — switching venues re-renders
  // this same instance with a filtered `slides` array (per-slide locationId
  // tags). A stored `trackIndex` that advanced past the new end would scroll
  // the track into empty space (blank hero) and never snap back. Clamp at the
  // derivation level so the rendered transform can never exceed the appended
  // clone at `slides.length`, without an effect or an extra render cycle.
  const safeTrackIndex = Math.min(trackIndex, slides.length);
  const currentSlide = safeTrackIndex % slides.length;

  const startAutoplay = useCallback(() => {
    clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      setAnimate(true);
      setTrackIndex((prev) => prev + 1);
    }, 4000);
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      setAnimate(true);
      setTrackIndex(index);
      startAutoplay();
    },
    [startAutoplay],
  );

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(autoplayRef.current);
  }, [startAutoplay]);

  // After the track lands on the clone (index === slides.length), wait for
  // the transition to end, then jump back to index 0 with animation off so
  // the snap is invisible.
  const handleTransitionEnd = useCallback(() => {
    if (safeTrackIndex === slides.length) {
      setAnimate(false);
      setTrackIndex(0);
    }
  }, [safeTrackIndex, slides.length]);

  // Re-enable transitions after the silent snap. requestAnimationFrame
  // ensures the browser has committed the snap before transitions resume.
  useEffect(() => {
    if (animate) return;
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [animate]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]!.clientX;
    touchDeltaX.current = 0;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    touchDeltaX.current = e.touches[0]!.clientX - touchStartX.current;
    setDragOffset(touchDeltaX.current);
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    const threshold = 50;
    if (touchDeltaX.current < -threshold) {
      goToSlide(safeTrackIndex + 1);
    } else if (touchDeltaX.current > threshold) {
      goToSlide((currentSlide - 1 + slides.length) % slides.length);
    }
    setDragOffset(0);
  }, [currentSlide, goToSlide, slides.length, safeTrackIndex]);

  const trackSlides = [...slides, slides[0]!];

  return (
    <section
      style={{
        position: 'relative',
        marginTop: 6,
        overflow: 'hidden',
        height: `${heightVh * 100}vh`,
      }}
    >
      <div
        dir="ltr"
        className="touch-pan-y"
        style={{ position: 'relative', width: '100%', height: '100%' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          onTransitionEnd={handleTransitionEnd}
          style={{
            display: 'flex',
            height: '100%',
            transform: `translate3d(calc(-${safeTrackIndex * 100}% + ${dragOffset}px), 0, 0)`,
            transition:
              dragOffset !== 0 || !animate ? 'none' : 'transform 400ms ease-out',
            willChange: 'transform',
          }}
        >
          {trackSlides.map((slide, index) => (
            <SlideLink
              key={index}
              href={slide.href}
              style={{
                position: 'relative',
                flex: '0 0 100%',
                height: '100%',
                display: 'block',
              }}
              ariaLabel={slide.headline ?? slide.label ?? slide.ctaLabel ?? t('common:carousel.slideAriaLabel', { index: index + 1 })}
              ariaHidden={index === slides.length ? true : undefined}
            >
              <SlideContent
                slide={slide}
                accountId={accountId}
                storageUrl={storageUrl}
                /* All carousel slides eager-load so the second slide is
                   ready the moment the transform reveals it — without
                   this, lazy-loading wouldn't trigger until the slide is
                   in viewport, which only happens after the transition
                   completes, so users see a blank frame. */
                priority
              />
            </SlideLink>
          ))}
        </div>
      </div>

      {/* Carousel dots — sit just above the caption strip */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 6,
          zIndex: 5,
        }}
      >
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            style={{
              height: 2,
              width: index === currentSlide ? 18 : 6,
              backgroundColor: index === currentSlide ? '#fff' : 'rgba(255,255,255,0.5)',
              border: 0,
              padding: 0,
              borderRadius: 999,
              transition: 'width 200ms ease-out',
              cursor: 'pointer',
            }}
            aria-label={t('common:carousel.goToSlideAriaLabel', { index: index + 1 })}
          />
        ))}
      </div>
    </section>
  );
}
